import React from 'react'
import ThemeInterface from '@/theme-interface'
import { MenuItem } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { NavLink } from 'react-router'
import clsx from 'clsx'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { useAppSelector } from '@/redux/store'

import { ColorModes } from '@/modules/layout/constants'
import useMenuPopover from '../useMenuPopover'
import { openFeatureLockedModal } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-locked-modal'
import { FeatureTypes, FeatureType } from '@/modules/base/components/modal/upgrade-modal/feature-locked/feature-config'
import { MenuItems } from '@/modules/base/util/constants'
import { isMeinBuero } from '@/utils/build-env'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.small,
    color: theme.mode.color.default,
    padding: '15px 30px',
    '&:hover': {
      color: theme.primaryBright,
      backgroundColor: 'unset',
      fontWeight: theme.fontWeights.medium,
    },
  },
  disabled: {
    cursor: 'pointer',
  },
  seperator: {
    borderBottom: `1px solid ${theme.mode.borderColor.medium}`,
    opacity: theme.mode.type === ColorModes.dark ? 0.1 : 1.0,
    margin: '0px 30px',
    '&:last-of-type': {
      display: 'none',
    },
  },
  active: {
    color: theme.primaryBright,
    fontWeight: theme.fontWeights.medium,
    '& $titleWrapper > $root': {
      color: theme.primaryBright,
      fontWeight: theme.fontWeights.medium,
    },
  },
  title: ({ isPurchasable }: { isPurchasable: boolean }) => ({
    opacity: isPurchasable ? 0.4 : 1,
    fontSize: theme.fontSizes.small,
  }),
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.lightBackground,
    padding: 4,
    borderRadius: 8,
    marginRight: 30,
  },
  icon: {
    fontSize: theme.fontSizes.smedium,
    color: theme.primary,
    height: 12,
    width: 12,
  },
}), { name: 'MenuPopoverItem' })

export interface SubmenuItemInterface {
  parentName: string,
  name: string,
  title: string,
  url: string,
  accessRights: [],
  isIncludedInPlan: boolean,
  read: boolean,
  write: boolean,
  purchasable: boolean,
  subscriptionModule: string,
}

const getFeatureTypeFromMenuItem = (menuItemName: string): FeatureType => {
  const featureTypeMap: Record<string, FeatureType> = {
    [MenuItems.Todos]: FeatureTypes.TODO,
    [MenuItems.Letters]: FeatureTypes.LETTERS,
    [MenuItems.LetterPaper]: FeatureTypes.LETTER_PAPERS,
    [MenuItems.Orders]: FeatureTypes.ORDERS_AND_DELIVERY_NOTES,
    [MenuItems.DeliveryNotes]: FeatureTypes.ORDERS_AND_DELIVERY_NOTES,
    [MenuItems.RecurringInvoices]: FeatureTypes.RECURRING_INVOICE,
    [MenuItems.Projects]: FeatureTypes.PROJECTS,
    [MenuItems.Banking]: FeatureTypes.BANKING,
    [MenuItems.Expenses]: FeatureTypes.EXPENSES,
    [MenuItems.Revenues]: FeatureTypes.REVENUE,
    [MenuItems.EconomicAssets]: FeatureTypes.ECONOMIC_ASSET,
    [MenuItems.Mailbox]: FeatureTypes.MAILBOX,
    [MenuItems.Timetracking]: FeatureTypes.TIMETRACKING,
    [MenuItems.Purchases]: FeatureTypes.PURCHASES,
    [MenuItems.TaxForms]: FeatureTypes.TAX_REPORT,
    [MenuItems.VatReturn]: FeatureTypes.TAX_REPORT,
    [MenuItems.DocumentExport]: FeatureTypes.TAX_REPORT,
    [MenuItems.Reporting]: FeatureTypes.TAX_REPORT,
    [MenuItems.EconomicAssets]: FeatureTypes.ECONOMIC_ASSET,
    [MenuItems.Booking]: FeatureTypes.TAX_REPORT,
  }

  return featureTypeMap[menuItemName] || FeatureTypes.ECONOMIC_ASSET
}

const MenuPopoverItem = (props: SubmenuItemInterface) => {
  const { name, title, url, read, write, purchasable } = props
  const { setOpen } = useMenuPopover()
  const showUnavailableFeatures = useAppSelector(state => state.user.info.layoutSettings?.showUnavailableFeatures ?? false)
  const classes = useStyles({ isPurchasable: purchasable })

  if ((purchasable || (!read && !write)) && (isMeinBuero() || !showUnavailableFeatures)) {
    return null
  }

  const onItemClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (purchasable) {
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
      const featureType = getFeatureTypeFromMenuItem(name)
      if (featureType) {
        openFeatureLockedModal(featureType)
      }
      return
    }
    setOpen(false)
  }

  return (
    <>
      <NavLink
        to={url}
        state={{ resetListPosition: true }}
        onClick={onItemClick}
        className={({ isActive }) => clsx({
          [classes.disabled]: purchasable,
          [classes.active]: !purchasable && isActive,
        })}
      >
        <div className={classes.titleWrapper}>
          <MenuItem
            data-nav="subMenuV2Item"
            title={title}
            data-qs-id={name}
            classes={{ root: classes.root }}
            onClick={onItemClick}
            data-pathname={url}>
            <span className={classes.title}>{title}</span>
          </MenuItem>
          {purchasable && (
            <div className={classes.iconContainer}>
              <RocketLaunchIcon className={classes.icon} />
            </div>
          )}
        </div>
      </NavLink>
      <div className={classes.seperator} />
    </>
  )
}

export default MenuPopoverItem
